/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, Chip, CircularProgress, IconButton, TextField, InputAdornment,
  Button, Typography, Table, TableBody, TableCell, TableRow, Alert,
  Dialog, DialogTitle, DialogContent, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../../api';

const toISO = (d) => d.toISOString().split('T')[0];

// ── Financial Year (India: 1-Apr to 31-Mar) helpers ───────────────
const FY_START_MONTH = 3; // April, 0-indexed
const fyStartYearForDate = (d) => (d.getMonth() >= FY_START_MONTH ? d.getFullYear() : d.getFullYear() - 1);
const fyRange = (startYear) => ({ from: `${startYear}-04-01`, to: `${startYear + 1}-03-31` });
const fyLabel = (startYear) => `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
const CURRENT_FY_START = fyStartYearForDate(new Date());
const FY_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_FY_START - i);
// Current FY runs to today (no future data); past FYs run the full year.
const fyDateWindow = (startYear) => {
  const { from, to } = fyRange(startYear);
  return { from, to: startYear === CURRENT_FY_START ? toISO(new Date()) : to };
};

const TEAL = '#00798E';
const TEAL_DARK = '#005f70';
const TEAL_LIGHT = '#e0f4f7';

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SOURCE_TYPES = ['BILL', 'PAYMENT', 'CREDIT_NOTE', 'DEBIT_NOTE', 'RETURN', 'MANUAL'];
const DEPARTMENTS = ['PHARMACY', 'LAB', 'DIAGNOSIS', 'OPD', 'GENERAL'];

// ── Journal Entry line-item dialog ──────────────────────────────

function JournalEntryDialog({ entryId, onClose }) {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entryId) return;
    setLoading(true);
    api.pharmacy.getJournalEntry(entryId)
      .then(res => setEntry(res.data || res))
      .finally(() => setLoading(false));
  }, [entryId]);

  return (
    <Dialog open={!!entryId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5,
        background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>{entry?.entry_no || 'Journal Entry'}</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.8)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 2.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: TEAL }} /></Box>
        ) : entry ? (
          <Box>
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #eee' }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{entry.narration}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.3 }}>
                {fmtDate(entry.entry_date)} · {entry.department} · Source: {entry.source_type}{entry.source_ref ? ` (${entry.source_ref})` : ''}
              </Typography>
            </Box>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ background: '#f0f4f8' }}>
                  <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase' }}>Account</TableCell>
                  <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase' }} align="right">Debit</TableCell>
                  <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase' }} align="right">Credit</TableCell>
                </TableRow>
                {(entry.lines || []).map(l => (
                  <TableRow key={l.id}>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{l.account?.name || '—'}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#aaa', fontFamily: 'monospace' }}>{l.account?.account_code}{l.narration ? ` · ${l.narration}` : ''}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 700, color: Number(l.dr_amount) > 0 ? '#2e7d32' : '#ccc' }}>
                      {Number(l.dr_amount) > 0 ? fmt(l.dr_amount) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 700, color: Number(l.cr_amount) > 0 ? '#c62828' : '#ccc' }}>
                      {Number(l.cr_amount) > 0 ? fmt(l.cr_amount) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ background: TEAL_LIGHT }}>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 800 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 800, color: TEAL_DARK }}>{fmt(entry.total_dr)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 800, color: TEAL_DARK }}>{fmt(entry.total_cr)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.82rem', color: '#aaa', textAlign: 'center', py: 3 }}>Entry not found</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Journal Entries tab ──────────────────────────────────────────

function JournalEntriesTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [financialYear, setFinancialYear] = useState(CURRENT_FY_START);
  const [fromDate, setFromDate] = useState(fyDateWindow(CURRENT_FY_START).from);
  const [toDate, setToDate] = useState(fyDateWindow(CURRENT_FY_START).to);
  const [department, setDepartment] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [error, setError] = useState('');
  const [viewEntryId, setViewEntryId] = useState(null);
  const searchTimer = useRef(null);
  const PER_PAGE = 25;

  const load = useCallback(async (q, fd, td, dept, st, pg) => {
    setLoading(true); setError('');
    try {
      const params = { page: pg, per_page: PER_PAGE };
      if (q) params.search = q;
      if (fd) params.from_date = fd;
      if (td) params.to_date = td;
      if (dept) params.department = dept;
      if (st) params.source_type = st;
      const res = await api.pharmacy.listJournalEntries(params);
      setRows(res.rows || []);
      setTotal(res.count || 0);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load journal entries');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(search, fromDate, toDate, department, sourceType, page); }, [page]); // eslint-disable-line

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(val, fromDate, toDate, department, sourceType, 1); }, 400);
  };
  const handleDateFilter = (fd, td) => { setFromDate(fd); setToDate(td); setPage(1); load(search, fd, td, department, sourceType, 1); };
  const handleDeptFilter = (v) => { setDepartment(v); setPage(1); load(search, fromDate, toDate, v, sourceType, 1); };
  const handleSourceFilter = (v) => { setSourceType(v); setPage(1); load(search, fromDate, toDate, department, v, 1); };
  const handleFyFilter = (fy) => {
    setFinancialYear(fy);
    const { from, to } = fyDateWindow(fy);
    handleDateFilter(from, to);
  };

  const totalPages = Math.ceil(total / PER_PAGE);
  const COL_HEADERS = ['Entry No', 'Date', 'Narration', 'Source', 'Dept', 'Debit', 'Credit', 'Status', ''];

  return (
    <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f4f8', background: '#fafcff' }}>
        <TextField
          size="small" placeholder="Search entry no, narration, ref…"
          value={search} onChange={e => handleSearchChange(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#b0bec5', fontSize: 18 }} /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.82rem', background: '#fff' } }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Financial Year</InputLabel>
          <Select value={financialYear} label="Financial Year" onChange={e => handleFyFilter(e.target.value)}>
            {FY_OPTIONS.map(fy => <MenuItem key={fy} value={fy}>{fyLabel(fy)}</MenuItem>)}
          </Select>
        </FormControl>
        <MDDatePicker label="From" value={fromDate} onChange={val => handleDateFilter(val || '', toDate)} sx={{ width: 145 }} />
        <MDDatePicker label="To" value={toDate} onChange={val => handleDateFilter(fromDate, val || '')} sx={{ width: 145 }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Department</InputLabel>
          <Select value={department} label="Department" onChange={e => handleDeptFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {DEPARTMENTS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Source</InputLabel>
          <Select value={sourceType} label="Source" onChange={e => handleSourceFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {SOURCE_TYPES.map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ m: 2, borderRadius: 2, fontSize: '0.82rem' }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
          <CircularProgress sx={{ color: TEAL }} size={36} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 950 }}>
            <TableBody>
              <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                {COL_HEADERS.map(h => (
                  <TableCell key={h} sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', textTransform: 'uppercase', letterSpacing: 0.7, py: 1.4, whiteSpace: 'nowrap', borderBottom: 'none' }}>{h}</TableCell>
                ))}
              </TableRow>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={9}>
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <AccountBalanceIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>No journal entries found</Typography>
                  </Box>
                </TableCell></TableRow>
              ) : rows.map((e, i) => (
                <TableRow key={e.id} hover sx={{ background: i % 2 === 0 ? '#fff' : '#fafcff', cursor: 'pointer', '&:hover': { background: TEAL_LIGHT } }}
                  onClick={() => setViewEntryId(e.id)}>
                  <TableCell sx={{ py: 1.3, fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 700, color: TEAL }}>{e.entry_no}</TableCell>
                  <TableCell sx={{ py: 1.3, fontSize: '0.76rem', color: '#546e7a', whiteSpace: 'nowrap' }}>{fmtDate(e.entry_date)}</TableCell>
                  <TableCell sx={{ py: 1.3, fontSize: '0.78rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.narration}</TableCell>
                  <TableCell sx={{ py: 1.3 }}>
                    <Chip label={e.source_type} size="small" sx={{ fontSize: '0.62rem', fontWeight: 700, height: 18, background: '#e3f2fd', color: '#1565c0' }} />
                  </TableCell>
                  <TableCell sx={{ py: 1.3, fontSize: '0.72rem', color: '#888' }}>{e.department}</TableCell>
                  <TableCell align="right" sx={{ py: 1.3, fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 700, color: '#2e7d32' }}>{fmt(e.total_dr)}</TableCell>
                  <TableCell align="right" sx={{ py: 1.3, fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 700, color: '#c62828' }}>{fmt(e.total_cr)}</TableCell>
                  <TableCell sx={{ py: 1.3 }}>
                    <Chip label={e.status} size="small" sx={{ fontSize: '0.6rem', height: 18, background: e.status === 'POSTED' ? '#e8f5e9' : '#fdecea', color: e.status === 'POSTED' ? '#2e7d32' : '#c62828' }} />
                  </TableCell>
                  <TableCell sx={{ py: 1.3 }}>
                    <IconButton size="small" sx={{ color: TEAL }}><VisibilityIcon sx={{ fontSize: 15 }} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.75, borderTop: '1px solid #f0f4f8', background: '#fafcff' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae' }}>
            Showing <b>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b>{total}</b>
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)} sx={{ border: '1.5px solid #e2e8f0', borderRadius: 2, color: page <= 1 ? '#d0d0d0' : TEAL }}>
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} sx={{ border: '1.5px solid #e2e8f0', borderRadius: 2, color: page >= totalPages ? '#d0d0d0' : TEAL }}>
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      <JournalEntryDialog entryId={viewEntryId} onClose={() => setViewEntryId(null)} />
    </Card>
  );
}

// ── Ledger Accounts tab ───────────────────────────────────────────

const ACC_TYPE_STYLE = {
  ASSET:     { bg: '#e8f5e9', color: '#2e7d32' },
  LIABILITY: { bg: '#fdecea', color: '#c62828' },
  INCOME:    { bg: '#e3f2fd', color: '#1565c0' },
  EXPENSE:   { bg: '#fff3e0', color: '#e65100' },
  EQUITY:    { bg: '#ede7f6', color: '#4527a0' },
};

function LedgerStatementDialog({ account, fromDate, toDate, fyStartYear, onClose }) {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!account) return;
    setLoading(true); setError('');
    api.pharmacy.getLedgerAccountStatement(account.id, { from_date: fromDate, to_date: toDate })
      .then(res => setStatement(res.data || res))
      .catch(e => setError(e?.response?.data?.message || e?.message || 'Failed to load ledger statement'))
      .finally(() => setLoading(false));
  }, [account, fromDate, toDate]);

  return (
    <Dialog open={!!account} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5,
        background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>{account?.account_code} · {account?.name}</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.75)' }}>{fyLabel(fyStartYear)} · {fmtDate(fromDate)} – {fmtDate(toDate)}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.8)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: '0.82rem' }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: TEAL }} /></Box>
        ) : statement ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ background: '#f0f4f8' }}>
                  <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase' }}>Date</TableCell>
                  <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase' }}>Narration</TableCell>
                  <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase' }} align="right">Debit</TableCell>
                  <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase' }} align="right">Credit</TableCell>
                  <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase' }} align="right">Balance</TableCell>
                </TableRow>
                <TableRow sx={{ background: TEAL_LIGHT }}>
                  <TableCell colSpan={4} sx={{ fontSize: '0.78rem', fontWeight: 700, color: TEAL_DARK }}>Opening Balance</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 800, color: TEAL_DARK }}>{fmt(statement.opening_balance)}</TableCell>
                </TableRow>
                {statement.lines.length === 0 ? (
                  <TableRow><TableCell colSpan={5}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#aaa', textAlign: 'center', py: 3 }}>No transactions in this period</Typography>
                  </TableCell></TableRow>
                ) : statement.lines.map(l => (
                  <TableRow key={l.id} hover>
                    <TableCell sx={{ py: 1, fontSize: '0.75rem', color: '#546e7a', whiteSpace: 'nowrap' }}>{fmtDate(l.entry_date)}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.78rem' }}>
                      {l.narration}
                      <Typography sx={{ fontSize: '0.65rem', color: '#aaa', fontFamily: 'monospace' }}>{l.entry_no} · {l.source_type}{l.source_ref ? ` (${l.source_ref})` : ''}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1, fontSize: '0.8rem', fontFamily: 'monospace', color: l.dr_amount > 0 ? '#2e7d32' : '#ccc' }}>{l.dr_amount > 0 ? fmt(l.dr_amount) : '—'}</TableCell>
                    <TableCell align="right" sx={{ py: 1, fontSize: '0.8rem', fontFamily: 'monospace', color: l.cr_amount > 0 ? '#c62828' : '#ccc' }}>{l.cr_amount > 0 ? fmt(l.cr_amount) : '—'}</TableCell>
                    <TableCell align="right" sx={{ py: 1, fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 700 }}>{fmt(l.running_balance)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ background: TEAL_LIGHT }}>
                  <TableCell colSpan={4} sx={{ fontSize: '0.8rem', fontWeight: 800 }}>Closing Balance</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 800, color: TEAL_DARK }}>{fmt(statement.closing_balance)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function LedgerAccountsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [financialYear, setFinancialYear] = useState(CURRENT_FY_START);
  const [error, setError] = useState('');
  const [statementAccount, setStatementAccount] = useState(null);

  const load = useCallback(async (q, fy) => {
    setLoading(true); setError('');
    try {
      const { from, to } = fyDateWindow(fy);
      const params = { from_date: from, to_date: to };
      if (q) params.search = q;
      const res = await api.pharmacy.listLedgerAccounts(params);
      setRows(res.data || res || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load ledger accounts');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(search, financialYear); }, [financialYear]); // eslint-disable-line

  const handleSearchChange = (val) => { setSearch(val); load(val, financialYear); };
  const { from: fyFrom, to: fyTo } = fyDateWindow(financialYear);

  const COL_HEADERS = ['Code', 'Account', 'Type', 'Normal Balance', 'Opening Balance', 'Closing Balance'];

  return (
    <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid #f0f4f8', background: '#fafcff' }}>
        <TextField
          size="small" placeholder="Search account name or code…"
          value={search} onChange={e => handleSearchChange(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#b0bec5', fontSize: 18 }} /></InputAdornment> }}
          sx={{ flex: 1, maxWidth: 340, '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.82rem', background: '#fff' } }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Financial Year</InputLabel>
          <Select value={financialYear} label="Financial Year" onChange={e => setFinancialYear(e.target.value)}>
            {FY_OPTIONS.map(fy => <MenuItem key={fy} value={fy}>{fyLabel(fy)}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ m: 2, borderRadius: 2, fontSize: '0.82rem' }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: TEAL }} size={36} /></Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableBody>
              <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                {COL_HEADERS.map(h => (
                  <TableCell key={h} sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', textTransform: 'uppercase', letterSpacing: 0.7, py: 1.4, whiteSpace: 'nowrap', borderBottom: 'none' }}>{h}</TableCell>
                ))}
              </TableRow>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#aaa', textAlign: 'center', py: 4 }}>No ledger accounts found</Typography>
                </TableCell></TableRow>
              ) : rows.map((a, i) => {
                const s = ACC_TYPE_STYLE[a.account_type] || { bg: '#f5f5f5', color: '#757575' };
                return (
                  <TableRow key={a.id} hover sx={{ background: i % 2 === 0 ? '#fff' : '#fafcff', cursor: 'pointer', '&:hover': { background: TEAL_LIGHT } }}
                    onClick={() => setStatementAccount(a)}>
                    <TableCell sx={{ py: 1.3, fontSize: '0.78rem', fontFamily: 'monospace', color: '#888' }}>{a.account_code}</TableCell>
                    <TableCell sx={{ py: 1.3, fontSize: '0.82rem', fontWeight: 700, color: '#2d3748' }}>{a.name}</TableCell>
                    <TableCell sx={{ py: 1.3 }}>
                      <Chip label={a.account_type} size="small" sx={{ fontSize: '0.62rem', fontWeight: 700, height: 18, background: s.bg, color: s.color }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.3, fontSize: '0.75rem', color: '#888' }}>{a.normal_balance}</TableCell>
                    <TableCell align="right" sx={{ py: 1.3, fontSize: '0.8rem', fontFamily: 'monospace', color: '#888' }}>
                      {fmt(a.opening_balance)}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.3, fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 800, color: Number(a.current_balance) >= 0 ? '#2e7d32' : '#c62828', pr: 2.5 }}>
                      {fmt(a.current_balance)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <LedgerStatementDialog
        account={statementAccount}
        fromDate={fyFrom}
        toDate={fyTo}
        fyStartYear={financialYear}
        onClose={() => setStatementAccount(null)}
      />
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────

export default function JournalLedgerPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('journal'); // journal | ledger

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={0}>
        {/* ── Hero Header ── */}
        <Box sx={{
          borderRadius: 4,
          background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 60%, #003d4d 100%)`,
          px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
          display: 'flex', alignItems: 'center', gap: 2,
          boxShadow: `0 8px 32px ${TEAL}44`,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <IconButton onClick={() => navigate(-1)}
            sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.28)' }, borderRadius: 2, zIndex: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ width: 46, height: 46, borderRadius: 2.5, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <AccountBalanceIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box sx={{ zIndex: 1 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Accounts</Typography>
            <Typography sx={{ color: '#fff', fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.1 }}>Journal &amp; Ledger</Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2, borderBottom: '2px solid #f0f0f0', pb: 0 }}>
          {[
            { key: 'journal', label: 'Journal Entries' },
            { key: 'ledger', label: 'Ledger Accounts' },
          ].map(t => (
            <Button key={t.key} size="small" onClick={() => setTab(t.key)}
              sx={{
                textTransform: 'none', fontWeight: tab === t.key ? 700 : 400,
                color: tab === t.key ? TEAL : '#888',
                borderBottom: tab === t.key ? `2px solid ${TEAL}` : '2px solid transparent',
                borderRadius: 0, px: 2, py: 1, mb: '-2px', fontSize: '0.82rem',
              }}>
              {t.label}
            </Button>
          ))}
        </Box>

        {tab === 'journal' ? <JournalEntriesTab /> : <LedgerAccountsTab />}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
